import "./styles.css";
import { loadPack, toast, clearPackCache } from "./api.js";
import { maybeShowTour } from "./tour.js";
import { renderHome, mountHome } from "./views/home.js";
import { renderPack } from "./views/pack.js";
import { renderReview, mountReview } from "./views/review.js";
import { renderLadder } from "./views/ladder.js";
import { renderPitch } from "./views/pitch.js";
import { renderCapture, mountCapture } from "./views/capture.js";
import { renderLeadsAdmin, mountLeadsAdmin } from "./views/leadsAdmin.js";

/** Lazy-load 3D brief (Google photorealistic default + schematic option) */
async function loadSite3d() {
  return import("./views/site3d.js");
}

const ROUTES = [
  { path: "/", label: "Overview", render: renderHome, mount: mountHome },
  { path: "/pack", label: "Leads", render: renderPack },
  { path: "/review", label: "Review", render: renderReview, mount: mountReview },
  {
    path: "/3d",
    label: "Site map",
    render: async (pack) => {
      const m = await loadSite3d();
      return m.renderSite3d(pack);
    },
    mount: async (pack) => {
      const m = await loadSite3d();
      return m.mountSite3d(pack);
    },
    unmount: async () => {
      const m = await loadSite3d();
      return m.unmountSite3d();
    },
  },
  { path: "/ladder", label: "Revenue", render: renderLadder },
  { path: "/pitch", label: "Pitch kit", render: renderPitch },
  { path: "/sales", label: "Revenue", render: renderLadder },
  {
    path: "/capture",
    label: "Get leads",
    render: renderCapture,
    mount: () => mountCapture(),
    public: true,
  },
  {
    path: "/leads",
    label: "Inbox",
    render: renderLeadsAdmin,
    mount: () => mountLeadsAdmin(),
  },
];

function currentPath() {
  // Support #/pack?market=seattle — strip query for route match
  let h = location.hash.replace(/^#/, "") || "/";
  if (!h.startsWith("/")) h = "/" + h;
  const q = h.indexOf("?");
  if (q >= 0) h = h.slice(0, q);
  return h || "/";
}

function renderNav(active) {
  const nav = document.getElementById("nav");
  if (!nav) return;
  const base = (active || "").split("?")[0];
  nav.innerHTML = ROUTES.filter((r) => r.path !== "/sales")
    .map(
      (r) =>
        `<a href="#${r.path}" class="${base === r.path || (r.path === "/ladder" && base === "/sales") ? "active" : ""}">${r.label}</a>`
    )
    .join("");
}

function setMeta(pack) {
  const el = document.getElementById("run-meta");
  if (!el || !pack) return;
  const where =
    pack.territory_id === "national"
      ? "Nationwide"
      : pack.territory_id || pack.metro || "Territory";
  const n = pack.lead_count ?? "—";
  el.textContent = `${where} · ${n} leads ready`;
  el.title = pack.run_id ? `Pack id ${pack.run_id}` : "";
}

let _pack = null;
let _activeUnmount = null;

function matchRoute(path) {
  const base = path.split("?")[0] || "/";
  return ROUTES.find((r) => r.path === base) || ROUTES[0];
}

async function route() {
  const path = currentPath();
  const route = matchRoute(path);
  renderNav(route.path);

  if (_activeUnmount) {
    try {
      _activeUnmount();
    } catch {
      /* ignore */
    }
    _activeUnmount = null;
  }

  const view = document.getElementById("view");
  if (!view) return;

  try {
    // Public capture works even if pack.json fails
    if (!route.public) {
      if (!_pack) {
        view.innerHTML = `<p class="muted">Loading pack…</p>`;
        _pack = await loadPack();
        setMeta(_pack);
      }
    } else if (!_pack) {
      try {
        _pack = await loadPack();
        setMeta(_pack);
      } catch {
        _pack = { lead_count: 0, disclaimer: "" };
      }
    }
    const html = await route.render(_pack);
    view.innerHTML = html;
    if (route.mount) {
      await route.mount(_pack);
      _activeUnmount = route.unmount || null;
    }
    // First-visit dealer tour (not on public capture form)
    if (!route.public) {
      maybeShowTour(_pack);
    }
  } catch (e) {
    console.error(e);
    if (route.public) {
      view.innerHTML = await route.render({});
      if (route.mount) await route.mount({});
      return;
    }
    view.innerHTML = `
      <h1>LeadForge</h1>

      <div class="panel">
        <p>Could not load <code>/data/pack.json</code>.</p>
        <p class="muted">${String(e.message || e)}</p>
        <p>From repo root:</p>
        <pre class="pre">python -m leadforge export-web
cd web
npm install
npm run dev</pre>
      </div>`;
    toast("Pack load failed", true);
  }
}

window.addEventListener("hashchange", () => {
  // Market query lives in the hash (?market= / ?zips=) — reload pack filter
  clearPackCache();
  _pack = null;
  route();
});
window.addEventListener("DOMContentLoaded", () => route());
